import { useEffect, useState } from "react";
import { X, Camera, Play } from "lucide-react";
import { api } from "@/lib/api";

const CATEGORIES = ["Máquinas", "Peças", "Serviços", "Estrutura"];
const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Fotos reais cadastradas via administração aparecem automaticamente aqui.
// Espaços reservados abaixo são exibidos enquanto cada categoria aguarda fotos.
const SLOTS = { Máquinas: 9, Peças: 6, Serviços: 6, Estrutura: 3 };

const slug = (s) => s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
const mediaUrl = (it) => `${API_BASE}/api/gallery/${it.id}/file`;

export default function Galeria() {
  const [cat, setCat] = useState("Todos");
  const [active, setActive] = useState(null);
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.get("/gallery").then(({ data }) => setItems(data)).catch(() => {});
  }, []);

  const photos = cat === "Todos" ? items : items.filter((p) => p.category === cat);
  const realCount = (c) => items.filter((p) => p.category === c).length;
  const cats = cat === "Todos" ? CATEGORIES : [cat];
  const slots = cats.flatMap((c) =>
    Array.from({ length: Math.max(0, SLOTS[c] - realCount(c)) }, (_, i) => ({ c, i }))
  );

  return (
    <div className="pt-[72px]" data-testid="galeria-page">
      <section className="py-20 border-b border-zinc-800 bg-zinc-900/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-3 mb-3">
            <span className="h-px w-10 bg-orange-500" />
            <span className="mono text-xs uppercase tracking-[0.35em] text-orange-400">nosso trabalho</span>
          </div>
          <h1 className="display text-4xl sm:text-6xl font-extrabold uppercase">Galeria</h1>
          <p className="mt-4 text-zinc-400 max-w-2xl text-sm sm:text-base">
            Máquinas, peças, serviços e estrutura da Alpha Metais — em breve com fotos reais da nossa oficina.
          </p>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-wrap gap-2" data-testid="gallery-filters">
            {["Todos", ...CATEGORIES].map((c) => (
              <button
                key={c}
                data-testid={`gallery-filter-${slug(c)}`}
                onClick={() => setCat(c)}
                className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm border transition-colors ${
                  cat === c
                    ? "bg-orange-600 border-orange-600 text-white"
                    : "border-zinc-700 text-zinc-400 hover:border-orange-500 hover:text-orange-400"
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="gallery-grid">
            {photos.map((p) => (
              <button
                key={p.id}
                data-testid={`gallery-photo-${p.id}`}
                onClick={() => p.media_type !== "video" && setActive(p)}
                className="group relative overflow-hidden rounded-sm border border-zinc-800 hover:border-orange-600 transition-colors text-left"
              >
                {p.file && p.media_type === "video" ? (
                  <video src={mediaUrl(p)} className="w-full object-cover h-44 sm:h-56" controls />
                ) : p.file ? (
                  <img
                    src={mediaUrl(p)}
                    alt={p.title}
                    loading="lazy"
                    className="w-full object-cover h-44 sm:h-56 group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="h-44 sm:h-56 grid place-content-center bg-zinc-900/50">
                    <Camera className="w-6 h-6 text-zinc-700" strokeWidth={1.25} />
                  </div>
                )}
                <span className="absolute bottom-2 left-2 mono text-[10px] uppercase tracking-widest bg-zinc-950/80 text-orange-400 px-2 py-1 rounded-sm">
                  {p.category}
                </span>
                {p.media_type === "video" && (
                  <span className="absolute top-2 right-2 bg-zinc-950/80 text-zinc-200 p-1.5 rounded-sm">
                    <Play className="w-3.5 h-3.5" />
                  </span>
                )}
                {(p.title || p.description) && (
                  <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-zinc-950/90 to-transparent px-3 pt-8 pb-2.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <span className="block text-xs font-semibold text-zinc-100 truncate">{p.title}</span>
                    {p.description && <span className="block text-[11px] text-zinc-400 line-clamp-1">{p.description}</span>}
                  </span>
                )}
              </button>
            ))}

            {slots.map(({ c, i }) => (
              <div
                key={`${c}-${i}`}
                data-testid={`gallery-slot-${slug(c)}-${i}`}
                className="rounded-sm border border-dashed border-zinc-800 bg-zinc-900/30 h-44 sm:h-56 flex flex-col items-center justify-center gap-2 text-zinc-700"
              >
                <Camera className="w-6 h-6" strokeWidth={1.25} />
                <span className="mono text-[10px] uppercase tracking-widest text-zinc-600">{c}</span>
                <span className="text-[10px] uppercase tracking-wider text-zinc-700">aguardando foto real</span>
              </div>
            ))}
          </div>

          <p className="mt-6 text-xs text-zinc-600 text-center" data-testid="gallery-note">
            Espaços preparados — as fotos reais da empresa, máquinas, peças e serviços serão publicadas em breve.
          </p>
        </div>
      </section>

      {active && (
        <div
          className="fixed inset-0 z-[60] bg-zinc-950/95 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setActive(null)}
          data-testid="gallery-lightbox"
        >
          <button
            data-testid="gallery-lightbox-close"
            className="absolute top-5 right-5 text-zinc-300 hover:text-orange-500 p-2"
            aria-label="Fechar"
          >
            <X className="w-7 h-7" />
          </button>
          <img src={mediaUrl(active)} alt={active.title} className="max-w-full max-h-[85vh] object-contain rounded-sm border border-zinc-800" />
          {active.title && (
            <div className="absolute bottom-6 inset-x-0 text-center">
              <div className="text-sm font-semibold text-zinc-100">{active.title}</div>
              {active.description && <div className="text-xs text-zinc-400 mt-1">{active.description}</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
